import { Card, Colors, Spinner, Classes, SpinnerSize } from "@blueprintjs/core";
import { ButtonsSection, FGCustomPanel, FGTextInput } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import assets from "../../assets";
import { SmallFormGenerator, PageBase } from "../../components";
import { useGlobalData } from "../../contexts";
import { useAuth } from "../../contexts";
import { useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { AuthenticateCommand, LanguageDto, EPlatform } from "../../api";
import { Link } from "react-router-dom";
import { ELoginRoutes } from "../../LoginRouter";

interface ILoginPageProps {}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 100px;
  & .${Classes.FORM_CONTENT} {
    width: 100%;
  }
`;

const InfoContainer = styled.div<{ error }>`
  color: ${({ error }) => (error ? Colors.RED1 : Colors.DARK_GRAY1)};
  width: 100%;
  text-align: center;
  font-style: italic;
  margin-bottom: 1em;
`;

const LinksContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const LoginPage: React.FunctionComponent<ILoginPageProps> = () => {
  const { isLoading: authLoading, login, loginError } = useAuth();
  const { t } = useTl();
  const globalData = useGlobalData();
  const { loading: systemInfoLoading, backendNotReacheable, shortVersion, retry } = useGlobalData();

  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      email: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)).required(t(ETLCodes.Login_EmailIsRequired)),
      password: Yup.string().nullable().required(t(ETLCodes.Login_PasswordIsRequired)),
    });
  }, [t]);

  const loginWithLanguage = React.useCallback(
    async (formikProps) => {
      const u = await login(formikProps);
      if (u) {
        globalData.setLanguage(LanguageDto.fromJS({ languageId: u.languageId, languageCode: u.languageCode }));
      }
    },
    [globalData, login]
  );

  return (
    <PageBase withCard={false} breadCrumbs={[]}>
      <Container>
        <ColumnContainer>
          <img src={assets.images.srshLogo} alt="SRSH_LOGO" style={{ height: "200px" }} />
          <h1>Care 4 Dogs</h1>
          <InfoContainer error={backendNotReacheable}>
            {systemInfoLoading ? (
              <Spinner size={SpinnerSize.SMALL} />
            ) : backendNotReacheable ? (
              <span onClick={retry}>Backend Server is not accessible !</span>
            ) : (
              shortVersion
            )}
          </InfoContainer>
          <Card elevation={4}>
            <SmallFormGenerator
              initialValues={AuthenticateCommand.fromJS({ login: "", password: "", platform: EPlatform.CARE4DOGS })}
              onSubmit={async (formikProps) => loginWithLanguage(formikProps)}
              labelAlignment="center"
              fill={true}
              inline={false}
              minLabelWidth={300}
              boldLabel={true}
              validationSchema={validationSchema}
              disabled={!!backendNotReacheable}
              hideButtons={true}
              showColons={false}
              autoRequiredMark={false}
              centerForm={true}
              enableDirtyCheck={false}
              preventDefaultSaveOnEnter={false}
            >
              <FGTextInput name="email" label={t(ETLCodes.Email)} />
              <FGTextInput name="password" label={t(ETLCodes.General_Password)} type="password" />
              <FGCustomPanel>
                {(ctx) => (
                  <LinksContainer>
                    <Link to={`${ELoginRoutes.forgottenPassword}`}>{t(ETLCodes.ForgottenPassword)}</Link>
                  </LinksContainer>
                )}
              </FGCustomPanel>
              <FGCustomPanel>
                {({ formik }) => (
                  <ButtonsSection
                    buttons={[
                      {
                        position: "center",
                        text: t(ETLCodes.LoginButton),
                        intent: "primary",
                        icon: "key",
                        type: "submit",
                        large: true,
                        loading: authLoading,
                        disabled: backendNotReacheable,
                      },
                    ]}
                  />
                )}
              </FGCustomPanel>
            </SmallFormGenerator>
            {loginError && (
              <InfoContainer error={loginError}>{loginError && <span>{t(ETLCodes.Error_Login)}</span>}</InfoContainer>
            )}
          </Card>
        </ColumnContainer>
      </Container>
    </PageBase>
  );
};
